import React, { useState, useRef, useContext, useEffect } from "react";
import { ResponsiveBarCanvas } from "@nivo/bar";
import { ProductsContext } from "../context/products.context";

// ==================================================================
// Main Component
// ==================================================================
const PerformanceIndicatorsGrid = ({
  indicatorData,
  indicatorsAggData,
  checkedStudentGroup,
  selectedFilterIndicatorSchool,
  selectedDistrictComparison,
  districtName,
  selectedFilterIndicatorGrade,
  selectedYear,
}) => {
  // Access the context to get the selected indicator
  const { indicatorsSubcategory, indicatorsCategory } = useContext(ProductsContext);
  
  // Debug mode toggle - disabled by default in production
  const [debug, setDebug] = useState(false);
  
  // Chart container ref
  const chartContainerRef = useRef(null);
  
  // Toggle debug mode with keyboard shortcut (Ctrl+D) - only for development/testing
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'd' && e.ctrlKey) {
        setDebug(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Early return if no data
  if (!indicatorData || !indicatorData.length) {
    return <div className="p-4">No indicator data found.</div>;
  }

  // Define year labels - fixed to specific years
  const yearDates = ["2021", "2022", "2023", "2024", "2025"];

  // Determine if we're working with Perkins indicators
  const isPerkinsCategory = indicatorsCategory?.value === "Perkins V Indicators";
  
  // Extract the indicator key (e.g., "1S1") from the subcategory value
  const indicatorKey = isPerkinsCategory && indicatorsSubcategory?.value
    ? indicatorsSubcategory.value.slice(0, 3)
    : null;
  
  // List of indicators that are district-level only
  const districtLevelOnlyIndicators = ["1S1", "1S2", "2S1"]; // Add others as needed
  
  // Determine if this is a district-level-only indicator
  const isDistrictOnly = isPerkinsCategory && 
    indicatorKey && 
    districtLevelOnlyIndicators.includes(indicatorKey);
  
  // Filter indicator data for current subcategory
  const filteredIndicatorData = isPerkinsCategory && indicatorKey
    ? indicatorData.filter((d) => d.subcategory === indicatorKey)
    : indicatorData;
  
  // Custom tooltip component for Nivo's BarCanvas
  const CustomTooltip = ({ data, id }) => {
    // Handle masked data - show special tooltip
    if (data.isMasked || data.allMasked) {
      return (
        <div style={{
          background: 'white',
          padding: '8px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          fontSize: '12px',
          lineHeight: '1.4'
        }}>
          <strong>{data.schoolYear}</strong>
          <div>Data is masked</div>
        </div>
      );
    }
    
    // Regular bar tooltip
    if (id === 'actualPct') {
      return (
        <div style={{
          background: 'white',
          padding: '8px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          fontSize: '12px',
          lineHeight: '1.4'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{data.schoolYear}</div>
          <div>{(data.actualPct * 100).toFixed(1)}%</div>
          <div>{data.numerators.actual !== null ? data.numerators.actual : '--'} / {data.denominators.actual !== null ? data.denominators.actual : '--'}</div>
        </div>
      );
    }
    
    return null;
  };

  // ==================================================================
  // Layer: Custom drawing on canvas for masked bars, labels, comparison lines
  // ==================================================================
  const customCanvasLayer = (ctx, { bars, yScale }) => {
    if (!bars?.length) return;
  
    const baseline    = yScale(0);
    const realBars    = bars.filter(b => !b.data.data.isMasked && b.height > 0);
    const minHeight   = realBars.length
      ? Math.min(...realBars.map(b => b.height))
      : 20;
    const lineSpacing = 6;
  
    // ── MASKED BARS ─────────────────────────────────────────────────────
    bars.forEach(bar => {
      const d = bar.data.data;
      if (!d.isMasked) return;
  
      const x = bar.x;
      const y = baseline - minHeight;
      const w = bar.width;
      const h = minHeight;
  
      // ① clip only the background + hatch
      ctx.save();
      ctx.beginPath();
      ctx.rect(x, y, w, h);
      ctx.clip();
  
      // background
      ctx.fillStyle = '#f8f8f8';
      ctx.fillRect(x, y, w, h);
  
      // diagonal hatch
      ctx.strokeStyle = '#e4e4e7';
      ctx.lineWidth   = 1;
      for (let xi = x - h; xi < x + w; xi += lineSpacing) {
        ctx.beginPath();
        ctx.moveTo(xi,     y);
        ctx.lineTo(xi + h, y + h);
        ctx.stroke();
      }
  
      ctx.restore();  // remove the clip
  
      // ② border (so it sits under the label)
      ctx.save();
      ctx.strokeStyle = '#e0e0e0';
      ctx.lineWidth   = 1;
      ctx.strokeRect(x, y, w, h);
      ctx.restore();
  
      // ③ “Masked” label last (top‑most)
      ctx.save();
      ctx.fillStyle    = '#999';
      ctx.font         = 'italic 12px sans-serif';
      ctx.textAlign    = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Masked', x + w/2, y + h/2);
      ctx.restore();
    });
  
    // ── VALUE LABELS FOR REAL BARS ──────────────────────────────────────
    bars.forEach(bar => {
      const d = bar.data.data;
      if (d.isMasked || bar.data.value == null) return;
  
      ctx.save();
      const label = `${Math.round(bar.data.value * 100)}%`;
  
      // white pill behind text
      ctx.fillStyle = '#fff';
      ctx.fillRect(
        bar.x + bar.width / 2 - 18,
        bar.y - 20,
        36,
        16
      );
  
      // text
      ctx.fillStyle    = '#333';
      ctx.font         = 'bold 12px sans-serif';
      ctx.textAlign    = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, bar.x + bar.width / 2, bar.y - 12);
      ctx.restore();
    });
  
    // ── COMPARISON LINES ────────────────────────────────────────────────
    bars.forEach(bar => {
      const d = bar.data.data;
      if (d.compPct == null) return;
  
      const y   = yScale(d.compPct);
      const x1  = bar.x - 10;
      const x2  = bar.x + bar.width + 10;
  
      ctx.save();
      ctx.setLineDash([6, 4]);
      ctx.strokeStyle = '#475569';
      ctx.lineWidth   = 2;
      ctx.beginPath();
      ctx.moveTo(x1, y);
      ctx.lineTo(x2, y);
      ctx.stroke();
  
      // little pill with the % value
      const pillW = 32,
            pillH = 16,
            pillX = bar.x + bar.width - 20,
            pillY = y - pillH / 2;
      ctx.fillStyle = '#475569';
      ctx.fillRect(pillX, pillY, pillW, pillH);
  
      ctx.fillStyle    = '#fff';
      ctx.font         = 'bold 11px sans-serif';
      ctx.textAlign    = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(
        `${Math.round(d.compPct * 100)}%`,
        pillX + pillW / 2,
        pillY + pillH / 2
      );
      ctx.restore();
    });
  };

  // ==================================================================
  // Nivo Theme Configuration
  // ==================================================================
  const theme = {
    axis: {
      fontSize: "14px",
      tickColor: "#9eb2d4",
      ticks: {
        line: { stroke: "#9eb2d4" },
        text: { fill: "#4f4f4f" },
      },
      legend: { text: { fill: "#a2a2a2" } },
    },
    grid: {
      line: { strokeWidth: 1, stroke: "#efefef" },
    },
  };

  // ==================================================================
  // Data Builder: Build single-key data with a separate comparison field
  // ==================================================================
  function buildSingleKeyData({
    indicatorData,
    indicatorsAggData,
    selectedFilterIndicatorSchool,
    selectedDistrictComparison,
    selectedFilterIndicatorGrade,
    yearDates,
    isDistrictOnly,
    studentGroup,
  }) {
    // Find the row for the main (actual) data
    const row = indicatorData.find(
      (f) =>
        f.studentGroup === studentGroup &&
        (isDistrictOnly || f.gradeLevel === selectedFilterIndicatorGrade.value)
    );
    // Find the comparison row
    const compRow = indicatorsAggData?.find(
      (agg) =>
        agg.studentGroup === studentGroup &&
        agg.gradeLevel === selectedFilterIndicatorGrade.value &&
        agg.districtName === selectedDistrictComparison.label
    );
    
    return yearDates
      .map((yearName, yearIndex) => {
        const builtObj = {
          schoolYear: yearName,
          actualPct: 0, // main bar value
          compPct: null, // comparison value (for dashed line and tooltip)
          numerators: {},
          denominators: {},
          studentGroup: studentGroup,
          isMasked: false,
          allMasked: false, // flag to know if all data points are masked
        };

        // Populate the main (actual) data
        if (row?.data?.length) {
          const foundItem = row.data.find(
            (z) => z.schoolName === selectedFilterIndicatorSchool.value
          );
          if (foundItem) {
            // Check for masked data - the -10 value is a signal for "masked"
            if (
              foundItem.denominator[yearIndex] == null ||
              foundItem.denominator[yearIndex] <= 0 ||
              (foundItem.pct[yearIndex] ?? 0) < 0 ||
              foundItem.pct[yearIndex] === -10
            ) {
              builtObj.isMasked = true;
              builtObj.actualPct = null; // Set to null instead of 0 for masked values
            } else {
              builtObj.actualPct = foundItem.pct[yearIndex] ?? 0;
            }

            builtObj.numerators.actual = foundItem.numerator[yearIndex] ?? null;
            builtObj.denominators.actual = foundItem.denominator[yearIndex] ?? null;
          }
        }

        // Populate the comparison data
        if (compRow?.pct) {
          // Similarly check for masked comparison data
          if (
            compRow.denominator?.[yearIndex] == null ||
            compRow.denominator?.[yearIndex] <= 0 ||
            (compRow.pct[yearIndex] ?? 0) < 0 ||
            compRow.pct[yearIndex] === -10
          ) {
            builtObj.compPct = null;
            builtObj.numerators.comp = null;
            builtObj.denominators.comp = null;
          } else {
            builtObj.compPct = compRow.pct[yearIndex] ?? null;
            builtObj.numerators.comp = compRow.numerator[yearIndex] ?? null;
            builtObj.denominators.comp = compRow.denominator[yearIndex] ?? null;
          }
        }

        return builtObj;
      })
      .filter(Boolean);
  }

  // Build chart data for one student group.
  const buildChartData = (studentGroup) => {
    const data = buildSingleKeyData({
      indicatorData: filteredIndicatorData,
      indicatorsAggData,
      selectedFilterIndicatorSchool,
      selectedDistrictComparison,
      selectedFilterIndicatorGrade,
      yearDates,
      studentGroup,
      isDistrictOnly, 
    });
    
    // Check if all bars in this chart are masked
    const allMasked = data.every(d => d.isMasked);
    if (allMasked) {
      data.forEach(d => {
        d.allMasked = true;
        d.actualPct = 0; // Set to 0 for proper display
      });
    }
    
    return data;
  };

  // Our chart will have one bar per row (the main value)
  const keys = ["actualPct"];

  // ==================================================================
  // Render: Map over checked student groups (one chart per group)
  // ==================================================================
  return (
    <div className="px-4 pb-10" ref={chartContainerRef} style={{ position: "relative" }}>
      <div className="flex flex-wrap gap-6 overflow-hidden">
        {checkedStudentGroup.map((sg) => {
          const dataForChart = buildChartData(sg);
          if (!dataForChart.length) return null;
          return (
            <div key={sg} className="border border-gray-200 rounded-lg p-2 shadow-sm">
              <h3 className="ml-4 mt-2 font-semibold ">{sg}</h3>
              <div 
                className="chart-container relative"
                style={{ width: 260, height: 161 }}
              >
                <ResponsiveBarCanvas
                  data={dataForChart}
                  keys={keys}
                  pixelRatio={3}
                  indexBy="schoolYear"
                  layout="vertical"
                  margin={{ top: 30, right: 20, bottom: 30, left: 40 }}
                  padding={0.25}
                  groupMode="grouped"
                  innerPadding={0}
                  valueScale={{ type: "linear" }}
                  indexScale={{ type: "band", round: true }}
                  minValue={0}
                  maxValue={1.0}
                  theme={theme}
                  colors={["#7dd3fc"]}
                  borderWidth={1}
                  borderRadius={2}
                  borderColor={{
                    from: 'color',
                    modifiers: [['darker', '0.55']],  
                  }}
                  enableLabel={false}
                  isInteractive={true}
                  tooltip={CustomTooltip}
                  tooltipPosition="bottom-right"
                  axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                  }}
                  axisLeft={{
                    format: (v) => `${(v * 100).toFixed(0)}%`,
                    tickSize: 5,
                    tickPadding: 5,
                  }}
                  enableGridX={false}
                  enableGridY={false}
                  labelSkipWidth={16}
                  labelSkipHeight={16}
                  layers={[
                    "grid",
                    "axes",
                    "bars",
                    customCanvasLayer, // Draws masked bars, labels, comparison lines
                  ]}
                />
                
                {/* Debug indicator - only in development */}
                {debug && (
                  <div style={{
                    position: 'fixed', 
                    top: '10px', 
                    left: '10px',
                    padding: '8px',
                    backgroundColor: 'red',
                    color: 'white',
                    fontWeight: 'bold',
                    zIndex: 9999,
                    fontSize: '11px',
                    borderRadius: '4px'
                  }}>
                    DEBUG MODE
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PerformanceIndicatorsGrid;